import { l as DocContext, o as Context, k as jsxRuntimeExports, t as textRendererStyle, n as DynamicMath } from "./doenetml-drNYjJiF.js";
import React__default, { useRef, useContext } from "react";
import { j as jxg } from "./JSXGraphRenderer-BcR36ozd.js";
import { BoardContext, TEXT_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { g as getPositionFromAnchorByCoordinate } from "./graph-W6vrFcBu.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { d as detachAnchoredGraphElement, a as attachAnchoredGraphDragHandlers } from "./useAnchoredGraphDragHandler-BEy2dmsI.js";
function getMathDelimiters(SVs) {
  if (SVs.renderMode === "inline") {
    return ["\\(", "\\)"];
  } else if (SVs.renderMode === "display") {
    return ["\\[", "\\]"];
  } else if (SVs.renderMode === "numbered") {
    return [`\\begin{gather}\\tag{${SVs.equationTag}}`, "\\end{gather}"];
  } else if (SVs.renderMode === "align") {
    return ["\\begin{align}", "\\end{align}"];
  }
  return ["\\(", "\\)"];
}
const math = React__default.memo(function MathComponent(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  MathComponent.ignoreActionsWithoutCore = () => true;
  const mathJXG = useRef(null);
  const anchorPointJXG = useRef(null);
  const anchorRel = useRef(null);
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  const pointerState = usePointerDragState();
  const pointAtDown = useRef(null);
  const calculatedX = useRef(null);
  const calculatedY = useRef(null);
  const previousPositionFromAnchor = useRef(null);
  const { fixed, fixLocation, lastPositionFromCore } = useDraggableRefs(SVs, null);
  useBoardPointerTracking(board, pointerState);
  useJSXGraphCleanup({
    objectRef: mathJXG,
    destroy: () => detachAnchoredGraphElement(mathJXG, board)
  });
  const { darkMode } = useContext(DocContext) || {};
  function createMathJXG() {
    if (board === null) {
      return null;
    }
    let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
    let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
    let cssStyle = ``;
    if (backgroundColor) {
      cssStyle += `background-color: ${backgroundColor}`;
    }
    let jsxMathAttributes = {
      visible: !SVs.hidden,
      fixed: fixed.current,
      layer: 10 * SVs.layer + TEXT_LAYER_OFFSET,
      cssStyle,
      highlightCssStyle: cssStyle,
      strokeColor: textColor,
      strokeOpacity: 1,
      highlightStrokeColor: textColor,
      highlightStrokeOpacity: 0.5,
      highlight: !fixLocation.current,
      useMathJax: true,
      parse: false
    };
    let newAnchorPointJXG;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      let anchorCoords = [
        anchor.get_component(0).evaluate_to_constant(),
        anchor.get_component(1).evaluate_to_constant()
      ];
      if (!Number.isFinite(anchorCoords[0])) {
        anchorCoords[0] = 0;
        jsxMathAttributes["visible"] = false;
      }
      if (!Number.isFinite(anchorCoords[1])) {
        anchorCoords[1] = 0;
        jsxMathAttributes["visible"] = false;
      }
      newAnchorPointJXG = board.create("point", anchorCoords, {
        visible: false
      });
    } catch (e) {
      jsxMathAttributes["visible"] = false;
      newAnchorPointJXG = board.create("point", [0, 0], {
        visible: false
      });
    }
    jsxMathAttributes.anchor = newAnchorPointJXG;
    let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
      SVs.positionFromAnchor
    );
    jsxMathAttributes.anchorx = anchorx;
    jsxMathAttributes.anchory = anchory;
    anchorRel.current = [anchorx, anchory];
    const [beginDelim2, endDelim2] = getMathDelimiters(SVs);
    let newMathJXG = board.create(
      "text",
      [0, 0, beginDelim2 + SVs.latex + endDelim2],
      jsxMathAttributes
    );
    attachAnchoredGraphDragHandlers({
      board,
      newJXG: newMathJXG,
      newAnchorPoint: newAnchorPointJXG,
      anchorRel,
      pointerState,
      pointAtDown,
      calculatedX,
      calculatedY,
      fixed,
      fixLocation,
      lastPositionFromCore,
      componentIdx,
      actions,
      callAction,
      actionNames: {
        move: "moveMath",
        focused: "mathFocused",
        clicked: "mathClicked"
      }
    });
    mathJXG.current = newMathJXG;
    anchorPointJXG.current = newAnchorPointJXG;
    previousPositionFromAnchor.current = SVs.positionFromAnchor;
    setTimeout(() => {
      if (mathJXG.current) {
        mathJXG.current.needsUpdate = true;
        mathJXG.current.setText(beginDelim2 + SVs.latex + endDelim2);
        mathJXG.current.update();
        board.updateRenderer();
      }
    }, 1e3);
  }
  if (board) {
    let anchorCoords;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
    } catch (e) {
      anchorCoords = [NaN, NaN];
    }
    lastPositionFromCore.current = anchorCoords;
    if (mathJXG.current === null) {
      createMathJXG();
    } else {
      mathJXG.current.relativeCoords.setCoordinates(
        jxg.COORDS_BY_USER,
        [0, 0]
      );
      anchorPointJXG.current?.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        anchorCoords
      );
      const [beginDelim2, endDelim2] = getMathDelimiters(SVs);
      mathJXG.current.setText(beginDelim2 + SVs.latex + endDelim2);
      let visible = !SVs.hidden;
      if (Number.isFinite(anchorCoords[0]) && Number.isFinite(anchorCoords[1])) {
        let actuallyChangedVisibility = mathJXG.current.visProp["visible"] !== visible;
        mathJXG.current.visProp["visible"] = visible;
        mathJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          mathJXG.current.setAttribute({ visible });
        }
      } else {
        mathJXG.current.visProp["visible"] = false;
        mathJXG.current.visPropCalc["visible"] = false;
      }
      let layer = 10 * SVs.layer + TEXT_LAYER_OFFSET;
      let layerChanged = mathJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        mathJXG.current.setAttribute({ layer });
      }
      let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
      let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
      let cssStyle = ``;
      if (backgroundColor) {
        cssStyle += `background-color: ${backgroundColor}`;
      } else {
        cssStyle += `background-color: transparent`;
      }
      if (mathJXG.current.visProp.strokecolor !== textColor) {
        mathJXG.current.visProp.strokecolor = textColor;
        mathJXG.current.visProp.highlightstrokecolor = textColor;
      }
      if (mathJXG.current.visProp.cssstyle !== cssStyle) {
        mathJXG.current.visProp.cssstyle = cssStyle;
        mathJXG.current.visProp.highlightcssstyle = cssStyle;
      }
      mathJXG.current.visProp.highlight = !fixLocation.current;
      mathJXG.current.visProp.fixed = fixed.current;
      mathJXG.current.isDraggable = !fixLocation.current;
      mathJXG.current.needsUpdate = true;
      if (SVs.positionFromAnchor !== previousPositionFromAnchor.current) {
        let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
          SVs.positionFromAnchor
        );
        mathJXG.current.visProp.anchorx = anchorx;
        mathJXG.current.visProp.anchory = anchory;
        anchorRel.current = [anchorx, anchory];
        previousPositionFromAnchor.current = SVs.positionFromAnchor;
        mathJXG.current.fullUpdate();
      } else {
        mathJXG.current.update();
      }
      if (anchorPointJXG.current) {
        anchorPointJXG.current.needsUpdate = true;
        anchorPointJXG.current.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  if (SVs.hidden) {
    return null;
  }
  const [beginDelim, endDelim] = getMathDelimiters(SVs);
  const latexWithDelims = beginDelim + SVs.latex + endDelim;
  let anchors = [];
  if (SVs.mrowChildRendererIds) {
    anchors.push(
      ...SVs.mrowChildRendererIds.map((rowId) => {
        return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id: rowId }, rowId);
      })
    );
  }
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    anchors,
    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: latexWithDelims }) })
  ] });
});
export {
  math as default
};
